import { useState } from 'react';
import { Trans } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  useCompaniesSuspenseQuery,
  useCreateCompanyInviteMutation,
  useCurrentUserSuspenseQuery,
  useDeleteCompanyUserMutation,
} from '../../../graphql/react-operations';
import { AlertConfirmDialog } from '../../../components/simple-dialogs/AlertConfirmDialog';
import { UserTableRow } from './UserTableRow';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { Typography } from '@startuptools/ui/Typography';
import { Card } from '@startuptools/ui/Card';
import { Table } from '@startuptools/ui/Table';
import { Chip } from '@startuptools/ui/Chip';
import { UserInvitesTable } from './UserInvitesTable';
import { PromptDialog } from '../../../components/simple-dialogs/PromptDialog';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { SettingsUsersDocument, useSettingsUsersSuspenseQuery } from './page.graphql';
import { toast } from '@startuptools/ui/toast/use-toast';
import { UserInvitesRequestsTable } from './UserInviteRequestsTable';
import { GqlCompanyUser } from '../../../graphql/base-types.graphql';
import { useNavigate } from '../../../components/Navigate';
import { useUserId } from '../../../components/react-wrapper/UserIdContext';

type FormValues = {
  email: string;
};

const UsersTable = () => {
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [userToDeleteId, setUserToDeleteId] = useState<string | null>(null);
  const { changeCompany, navigate } = useNavigate();
  const userId = useUserId();
  const companyId = useCompanyId();

  const {
    data: { companyInvites, companyInviteRequests, companyUsers },
    refetch,
  } = useSettingsUsersSuspenseQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });

  const { data: companyData } = useCompaniesSuspenseQuery();

  const {
    data: { currentUser },
  } = useCurrentUserSuspenseQuery();

  // eslint-disable-next-line sut/loading-mutation
  const [doCreateCompanyInvite] = useCreateCompanyInviteMutation({ refetchQueries: [SettingsUsersDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [deleteCompanyUserMutation] = useDeleteCompanyUserMutation();

  const { register, handleSubmit, reset: resetForm } = useForm<FormValues>();

  const handleDeleteUserClick = (companyUser: Pick<GqlCompanyUser, 'id'>) => {
    setUserToDeleteId(companyUser.id);
    setIsConfirmDialogOpen(true);
  };

  const onSubmitInviteForm: SubmitHandler<FormValues> = async values => {
    await doCreateCompanyInvite({ variables: { companyId, input: values } });
    setShowInviteUser(false);
    resetForm();
    return;
  };

  const handleConfirmDelete = async () => {
    if (userToDeleteId) {
      try {
        await deleteCompanyUserMutation({ variables: { companyId, id: userToDeleteId } });
        const company = companyData.companies.filter(c => c.id !== companyId)[0];
        const cu = companyUsers.filter(cu => cu.id === userToDeleteId)[0];
        if (cu?.userId !== userId) {
          await refetch();
        } else {
          if (company) {
            await changeCompany(company.id);
          } else {
            await navigate('/onboarding/create-company');
          }
        }
      } catch {
        toast({
          title: <Trans id="Error!" />,
          description: (
            <Trans id="An error occurred while deleting the requested user. Try it again and if the error persists, contact our customer support." />
          ),
          severity: 'danger',
        });
      }

      setIsConfirmDialogOpen(false);
      setUserToDeleteId(null);
    }
  };

  return (
    <>
      <Stack gap={2}>
        <Stack direction="row">
          <Button onClick={() => setShowInviteUser(true)}>
            <Trans id="Invite company admin" />
          </Button>
        </Stack>
        <Card>
          <Table sx={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>
                  <Trans id="User" />
                </th>
                <th>
                  <Trans id="Role" />
                </th>
                {currentUser.admin && (
                  <>
                    <th>
                      <Trans
                        id="Personal ID <chip>admin</chip>"
                        components={{
                          chip: <Chip size="sm" variant="outlined" />,
                        }}
                      />
                    </th>
                    <th>
                      <Trans
                        id="Email <chip>admin</chip>"
                        components={{
                          chip: <Chip size="sm" variant="outlined" />,
                        }}
                      />
                    </th>
                    <th>
                      <Trans
                        id="Phone <chip>admin</chip>"
                        components={{
                          chip: <Chip size="sm" variant="outlined" />,
                        }}
                      />
                    </th>
                  </>
                )}
                <th />
              </tr>
            </thead>
            <tbody>
              {companyUsers.map(cu => (
                <UserTableRow
                  key={cu.id}
                  companyUser={cu}
                  handleDeleteUserClick={handleDeleteUserClick}
                  showDeleteUserButton={companyUsers.length > 1}
                  currentUser={currentUser}
                />
              ))}
            </tbody>
          </Table>
        </Card>
        {companyInvites.length > 0 && <UserInvitesTable invites={companyInvites} />}
        {companyInviteRequests.length > 0 && <UserInvitesRequestsTable invites={companyInviteRequests} />}
      </Stack>
      {showInviteUser && (
        <PromptDialog
          title={<Trans id="Send invititation to..." />}
          onCancel={() => {
            setShowInviteUser(false);
            resetForm();
          }}
          onSubmit={handleSubmit(onSubmitInviteForm)}
          loading={false}
        >
          <FormControl required>
            <FormLabel>
              <Trans id="Email" />
            </FormLabel>
            <Input {...register('email')} type="email" required placeholder="name@example.com" />
          </FormControl>
        </PromptDialog>
      )}
      {isConfirmDialogOpen && (
        <AlertConfirmDialog
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          title={<Trans id="Delete User" />}
          content={<Trans id="Are you sure you want to delete this user?" />}
        />
      )}
    </>
  );
};

export const CompanySettingsUsers = () => {
  return (
    <Stack>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography level="h2">
          <Trans id="Company's Users" />
        </Typography>
      </Box>
      <ErrorBoundary>
        <UsersTable />
      </ErrorBoundary>
    </Stack>
  );
};
